import { useState, useEffect } from 'react'
import { Button, Tooltip, Typography, useTheme } from '@mui/material'
import InCardStack from 'components/atoms/container/InCardStack'
import BoxWithBorder from 'components/atoms/container/BoxWithBorder'
import CharacterAvatar from './CharacterAvatar'
import colors from 'styles/colors'
import RowStack from 'components/atoms/container/RowStack'
import { useTranslation } from 'react-i18next'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { ROLE_PLAY_TYPES } from 'services/constants'
import { convertSecsToMins } from 'utils/formatDateTime'
import { botName } from 'utils/formatText'
import RolePlayCallVoiceVisualizer from './RolePlayCallVoiceVisualizer'
import TextsmsOutlinedIcon from '@mui/icons-material/TextsmsOutlined'
import CallOutlinedIcon from '@mui/icons-material/CallOutlined'
import { IconContainer } from 'components/atoms/Icons'

const RolePlayInteractionCard = ({
  rolePlay,
  buttonText,
  onButtonClick,
  children,
  showAudioIcon,
  type,
  completeTime,
  callTimer,
  inPopup = false,
  callClient,
  btnProps = {},
  isStarted = false,
  setIsStarted = () => {},
  setPracticeType = () => {},
  isCompleted = false,
  isShowResults = false,
  tryRolePlayAgain = () => {}
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [isBotSpeaking, setIsBotSpeaking] = useState(true)
  const [isBotReady, setIsBotReady] = useState(false)
  const [showPracticeTypeSelect, setShowPracticeTypeSelect] = useState(false)

  useEffect(() => {
    setShowPracticeTypeSelect(
      !isStarted && !inPopup && !isCompleted && !isShowResults
    )
  }, [isStarted, inPopup, isCompleted, isShowResults])

  const startRolePlay = (selectedType = type) => {
    setPracticeType(selectedType)
    setIsStarted(true)
  }

  const InteractionButton = ({ icon, text, ...otherProps }) => (
    <Button
      startIcon={icon ? <IconContainer icon={icon} /> : null}
      color='muted'
      sx={{
        backgroundColor: colors.MetalGrey50
      }}
      {...otherProps}>
      {text}
    </Button>
  )

  return (
    <BoxWithBorder
      sx={{
        border: 1,
        borderStyle: inPopup ? 'none' : undefined,
        borderColor: theme.palette.primary.main,
        background: inPopup
          ? colors.Transparent
          : `linear-gradient(to top, ${theme.palette.primary.main}, ${colors.DarkBlue})`,
        borderWidth: 1,
        display: 'flex',
        alignItems: 'center',
        paddingY: 24,
        width: '100%',
        color: inPopup ? 'unset' : theme.palette.background.default
      }}>
      <InCardStack width='100%' spacing={28}>
        <InCardStack width='100%' spacing={8}>
          <CharacterAvatar
            rolePlay={rolePlay}
            name={botName(rolePlay)}
            size={160}
            sx={{
              borderWidth: 6,
              borderStyle: 'solid',
              borderColor: inPopup ? colors.Black : colors.White
            }}
            showAudioIcon={showAudioIcon && isBotReady}
            showCompleteIcon={inPopup}
            isBotSpeaking={isBotSpeaking}
          />
          <RolePlayCallVoiceVisualizer
            client={callClient}
            isBotReady={isBotReady}
            setIsBotReady={setIsBotReady}
            isBotSpeaking={isBotSpeaking}
            setIsBotSpeaking={setIsBotSpeaking}
          />
          <RowStack width='fit-content' spacing={2}>
            {inPopup ? (
              <Typography variant='h3' paddingX={10} textAlign='center'>
                {rolePlay.title} {t('superLeaderUserDrillDown.complete')}
              </Typography>
            ) : (
              <Typography variant='h1' color={colors.White}>
                {botName(rolePlay)}
              </Typography>
            )}
            {!inPopup && (
              <Tooltip
                title={t('rolePlay.powerByAI', {
                  botName: botName(rolePlay).split(' ')[0]
                })}>
                <InfoOutlinedIcon />
              </Tooltip>
            )}
          </RowStack>

          {callTimer && (
            <Typography variant='body1' color='Background'>
              {callTimer}
            </Typography>
          )}

          {completeTime && (
            <Typography color={colors.White}>
              {`${t(type === ROLE_PLAY_TYPES.LIVE_CALL ? 'rolePlay.call' : 'rolePlay.textChat')} ${t('common.completed')} `}
              {type === ROLE_PLAY_TYPES.LIVE_CALL &&
                `(${convertSecsToMins(completeTime).trim()})`}
            </Typography>
          )}
          {buttonText && (
            <Button onClick={onButtonClick} {...btnProps}>
              {buttonText}
            </Button>
          )}

          {isShowResults && (
            <InteractionButton
              text={t('rolePlay.tryAgain')}
              onClick={tryRolePlayAgain}
            />
          )}

          {children}
        </InCardStack>

        {showPracticeTypeSelect && (
          <RowStack spacing={6} justifyContent='center'>
            <InteractionButton
              icon={<IconContainer icon={<TextsmsOutlinedIcon />} />}
              text={t('rolePlay.textChat')}
              onClick={() => startRolePlay(ROLE_PLAY_TYPES.TEXT_CHAT)}
            />
            <InteractionButton
              icon={<IconContainer icon={<CallOutlinedIcon />} />}
              text={t('rolePlay.call')}
              onClick={() => startRolePlay(ROLE_PLAY_TYPES.LIVE_CALL)}
            />
          </RowStack>
        )}
      </InCardStack>
    </BoxWithBorder>
  )
}

export default RolePlayInteractionCard
